import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";

import _find from "lodash/find";
import _get from "lodash/get";

import settingAction from "modules/setting/actions";
import settingSelectors from "modules/setting/selectors";
import AdsSelector from "../components/wizard/AdsSelector";
import AdsConfirmation from "../components/wizard/AdsConfirmation";
import Section from "components/Section";
import Row from "components/Row";
import Column from "components/Column";
import Icon from "components/Icon";
import Button from "components/Buttons";
import Dropdown from "components/Dropdown";
import { Text, TextHeader } from "components/Texts";
import Toast from "components/Toast";
import GoogleSetup from "components/gads/Setup";
import GoogleAdsSetup from "components/gads/AdsSetup";
import GoogleAdsConnected from "components/gads/Connected";
import FacebookSetup from "components/facebook/Setup";
import FacebookAdsSetup from "components/facebook/AdsSetup";
import FacebookAdsConnected from "components/facebook/AdsConnected";
import FacebookAdsOfflineConversion from "components/facebook/OfflineConversion";
import Loading from "components/gads/Loading";
import GoogleAdsOfflineConversion from "components/gads/OfflineConversion";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import colors from "styles/colors";
import useWindowSize from "../layout/useWindowSize";
import mainSelectors from "../modules/main/selectors";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import FacebookLeadForms from "components/facebook/LeadForms";
import GoogleLeadForms from "components/gads/LeadForms";
import FacebookAdsCRMConversion from "components/facebook/CRMConversion";

const Setting = () => {
  const { REACT_APP_RMP_APP_ID, REACT_APP_ENABLE_GOOGLE, REACT_APP_ENABLE_GOOGLE_WHITELIST } = process.env;
  const [t] = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useWindowSize().width < 835;
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const rmpToken = sessionStorage.getItem("rmp_token");
  const settingMenu = sessionStorage.getItem("selectedMenu");
  const emailStatus = sessionStorage.getItem("email");
  const currentDashboard = useSelector((state) => mainSelectors.currentAd(state));
  const authAdproResult = useSelector((state) => mainSelectors.authAdproResult(state));
  const changeFacebookSettingPageResult = useSelector((state) => facebookSelectors.changeSettingPageResult(state));
  const changeSettingPageResult = useSelector((state) => googleSelectors.changeSettingPageResult(state));
  const businessInfoResult = useSelector((state) => googleSelectors.getBusinessInfoResult(state));
  const accountDataResult = useSelector((state) => googleSelectors.getAccountDataResult(state));
  const showNoAdsToastResult = useSelector((state) => googleSelectors.showNoAdsToastResult(state));
  const facebookSettingsResult = useSelector((state) => facebookSelectors.getFacebookSettingsResult(state));
  const [isSelfService, setIsSelfService] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("google_ads_setup");
  const [selectedMobileFacebookMenu, setSelectedMobileFacebookMenu] = useState("facebook_ads_setup");
  const [selectedMobileGoogleMenu, setSelectedMobileGoogleMenu] = useState("google_ads_setup");
  const [mobileServiceIndex, setMobileServiceIndex] = useState(0);
  const RcrmAppAvailableResult = useSelector((state) => mainSelectors.checkAppAvailableResult(state));

  useEffect(() => {
    sessionStorage.setItem("selectedMenu", selectedMenu);
    sessionStorage.setItem("selectedMobileFacebookMenu", selectedMobileFacebookMenu);
    sessionStorage.setItem("selectedMobileGoogleMenu", selectedMobileGoogleMenu);
  });
  const backToDashboard = () => {
    history.push("/dashboard");
  };

  const noAdsToastCallback = (value) => {
    dispatch(googleActions.showNoAdsToast(value));
  };

  useMemo(() => {
    if (authAdproResult.result === 0) {
      setIsSelfService(false);
    } else {
      setIsSelfService(true);
    }
  }, [authAdproResult]);

  const getAccountData = async () => {
    const result = await dispatch(
      googleActions.getAccountData({
        token: rmpToken,
        business_id: businessInfoResult.data.business_id,
        agency_id: businessInfoResult.data.agency_id,
        adwords_id: businessInfoResult.data.adwords_id,
      }),
    );

    if (
      (result.result === 0 ||
        (result.result !== 0 && businessInfoResult.data.adwords_id && businessInfoResult.data.adwords_id !== "-")) &&
      isSelfService === true
    ) {
      dispatch(googleActions.changeSettingPage("connected"));
      if (emailStatus == "google_leads") {
        setSelectedMenu("google_lead_form");
        setSelectedMobileGoogleMenu("google_lead_form");
        sessionStorage.setItem("selectedMenu", "google_lead_form");
      }
    } else if (result.result !== 0 && isSelfService === true) {
      dispatch(googleActions.changeSettingPage("signin"));
      if (emailStatus == "google_leads") {
        setSelectedMenu("google_ads_setup");
        setSelectedMobileGoogleMenu("google_ads_setup");
        sessionStorage.setItem("selectedMenu", "google_ads_setup");
      }
    } else if (result.result === 0 && isSelfService === false) {
      dispatch(googleActions.changeSettingPage("connected"));
    }

    setLoading(false);
  };

  useEffect(() => {
    if (location.pathname === "/setting/fb") {
      const facebookMenu = [
        "facebook_ads_setup",
        "facebook_ads_web_conversion",
        "facebook_ads_crm_conversion",
        "facebook_lead_form",
      ];
      if (settingMenu !== "" && settingMenu != null && facebookMenu.includes(settingMenu)) {
        changeTab(settingMenu);
        setSelectedMenu(settingMenu);
        setSelectedMobileFacebookMenu(settingMenu);
      } else {
        changeTab("facebook_ads_setup");
        setSelectedMenu("facebook_ads_setup");
        setSelectedMobileFacebookMenu("facebook_ads_setup");
      }
      setMobileServiceIndex(1);
    }
  }, []);

  // Check Google Ads Connection
  useEffect(() => {
    dispatch(
      googleActions.getBusinessInfo(rmpToken, {
        appId: REACT_APP_RMP_APP_ID,
      }),
    );
  }, []);

  useEffect(() => {
    if (businessInfoResult.data.business_id) {
      getAccountData();
    }
  }, [businessInfoResult]);

  useEffect(() => {
    getFacebookSettings();
  }, []);

  const getFacebookSettings = async () => {
    const result = await dispatch(
      facebookActions.getFacebookSettings({
        token: rmpToken,
      }),
    );

    if (
      result.result === 0 &&
      result.data.length > 0 &&
      result.data[0].fb_ads_business_id &&
      result.data[0].setup_status === "completed"
    ) {
      dispatch(facebookActions.changeSettingPage("connected"));
      if (emailStatus == "meta_leads") {
        setSelectedMenu("facebook_lead_form");
        setSelectedMobileFacebookMenu("facebook_lead_form");
        sessionStorage.setItem("selectedMenu", "facebook_lead_form");
      }
    } else {
      dispatch(facebookActions.changeSettingPage("signin"));
      if (emailStatus == "meta_leads") {
        setSelectedMenu("facebook_ads_setup");
        setSelectedMobileFacebookMenu("facebook_ads_setup");
        sessionStorage.setItem("selectedMenu", "facebook_ads_setup");
      }
    }
    dispatch(facebookActions.getDetailsPixelId({ token: rmpToken }));
  };

  const renderGoogleAdsSetup = () => {
    if (authAdproResult.result === 0) {
      if (changeSettingPageResult.data.page === "connected") {
        return <GoogleAdsConnected />;
      } else {
        return <AdsAccountSetting />;
      }
    } else {
      if (changeSettingPageResult.data.page === "ads_setup") {
        return <GoogleAdsSetup />;
      } else if (changeSettingPageResult.data.page === "signin") {
        return <GoogleSetup />;
      } else if (changeSettingPageResult.data.page === "connected") {
        return <GoogleAdsConnected />;
      }
    }
  };

  const renderGoogleAdsConversions = () => {
    if (
      accountDataResult.result !== 0 &&
      (!businessInfoResult.data.adwords_id || businessInfoResult.data.adwords_id === "-") &&
      changeSettingPageResult.data.page === "ads_setup" &&
      isSelfService === true
    ) {
      return <GoogleAdsSetup />;
    } else if (
      accountDataResult.result !== 0 &&
      (!businessInfoResult.data.adwords_id || businessInfoResult.data.adwords_id === "-") &&
      changeSettingPageResult.data.page === "signin" &&
      isSelfService === true
    ) {
      return <GoogleSetup />;
    } else {
      return <GoogleAdsOfflineConversion />;
    }
  };

  const renderFacebookAdsSetup = () => {
    if (changeFacebookSettingPageResult.data.page === "ads_setup") {
      return <FacebookAdsSetup />;
    } else if (changeFacebookSettingPageResult.data.page === "signin") {
      return <FacebookSetup />;
    } else if (changeFacebookSettingPageResult.data.page === "connected") {
      return <FacebookAdsConnected />;
    } else {
      return <></>;
    }
  };

  const renderFacebookAdsConversions = () => {
    if (false) {
    } else {
      return <FacebookAdsOfflineConversion />;
    }
  };

  const renderFacebookAdsCRMConversions = () => {
    if (false) {
    } else {
      return <FacebookAdsCRMConversion />;
    }
  };

  const renderFacebookLeadForm = () => {
    return <FacebookLeadForms />;
  };

  const renderGoogleLeadForm = () => {
    return <GoogleLeadForms />;
  };

  const RenderContent = useMemo(() => {
    if (loading === true) {
      return <Loading />;
    } else {
      if (selectedMenu === "google_ads_setup") {
        return renderGoogleAdsSetup();
      } else if (selectedMenu === "google_ads_offline_conversion") {
        return renderGoogleAdsConversions();
      } else if (selectedMenu === "google_lead_form") {
        return renderGoogleLeadForm();
      } else if (selectedMenu === "facebook_ads_setup") {
        return renderFacebookAdsSetup();
      } else if (selectedMenu === "facebook_ads_web_conversion") {
        return renderFacebookAdsConversions();
      } else if (selectedMenu === "facebook_ads_crm_conversion") {
        return renderFacebookAdsCRMConversions();
      } else if (selectedMenu === "facebook_lead_form") {
        return renderFacebookLeadForm();
      }
    }
  }, [authAdproResult, changeSettingPageResult, changeFacebookSettingPageResult, selectedMenu, loading]);

  const RenderGoogleAdsContent = useMemo(() => {
    if (loading === true) {
      return <Loading />;
    } else {
      if (selectedMobileGoogleMenu === "google_ads_setup") {
        return renderGoogleAdsSetup();
      } else if (selectedMobileGoogleMenu === "google_ads_offline_conversion") {
        return renderGoogleAdsConversions();
      } else if (selectedMobileGoogleMenu === "google_lead_form") {
        return renderGoogleLeadForm();
      }
    }
  }, [authAdproResult, changeSettingPageResult, selectedMobileGoogleMenu, loading]);

  const RenderFacebookAdsContent = useMemo(() => {
    if (selectedMobileFacebookMenu === "facebook_ads_setup") {
      return renderFacebookAdsSetup();
    } else if (selectedMobileFacebookMenu === "facebook_ads_web_conversion") {
      return renderFacebookAdsConversions();
    } else if (selectedMobileFacebookMenu === "facebook_ads_crm_conversion") {
      return renderFacebookAdsCRMConversions();
    } else if (selectedMobileFacebookMenu === "facebook_lead_form") {
      return renderFacebookLeadForm();
    }
  }, [changeFacebookSettingPageResult, selectedMobileFacebookMenu]);

  const changeTab = async (tab) => {
    if (!["facebook_ads_setup", "facebook_ads_web_conversion", "facebook_ads_crm_conversion", "facebook_lead_form"].includes(tab)) {
      if (
        facebookSettingsResult.data &&
        facebookSettingsResult.data[0] &&
        facebookSettingsResult.data[0].setup_status === "completed"
      ) {
        dispatch(facebookActions.changeSettingPage("connected"));
      } else {
        dispatch(facebookActions.changeSettingPage("signin"));
      }
    }

    if (tab === "google_ads_offline_conversion" && accountDataResult.result === 0 && accountDataResult.adwords_id) {
      setSelectedMenu("google_ads_offline_conversion");
      setSelectedMobileGoogleMenu("google_ads_offline_conversion");
    } else if (tab === "google_ads_setup") {
      setSelectedMenu("google_ads_setup");
      setSelectedMobileGoogleMenu("google_ads_setup");
    } else if (tab === "google_lead_form" && accountDataResult.result === 0 && accountDataResult.adwords_id) {
      setSelectedMenu("google_lead_form");
      setSelectedMobileGoogleMenu("google_lead_form");
      setLoading(false);
    } else if (tab === "facebook_ads_setup") {
      setSelectedMenu("facebook_ads_setup");
      setSelectedMobileFacebookMenu("facebook_ads_setup");
      setLoading(false);
    } else if (
      tab === "facebook_ads_web_conversion" &&
      facebookSettingsResult.result === 0 &&
      facebookSettingsResult.data.length > 0 &&
      facebookSettingsResult.data[0].setup_status === "completed"
    ) {
      setSelectedMenu("facebook_ads_web_conversion");
      setSelectedMobileFacebookMenu("facebook_ads_web_conversion");
      setLoading(false);
    } else if (
      tab === "facebook_ads_crm_conversion" &&
      facebookSettingsResult.result === 0 &&
      facebookSettingsResult.data.length > 0 &&
      facebookSettingsResult.data[0].setup_status === "completed"
    ) {
      setSelectedMenu("facebook_ads_crm_conversion");
      setSelectedMobileFacebookMenu("facebook_ads_crm_conversion");
      /** add loading before show crm conversions */
      await dispatch(facebookActions.setCRMConversionsLoading(true));
      const pixelId = await dispatch(facebookActions.getDetailsPixelId({ token: rmpToken }));
      if (pixelId.result === 0) {
        await dispatch(facebookActions.setCRMConversionsLoading(false));
      } else {
        await dispatch(facebookActions.setCRMConversionsLoading(false));
      }
      /** add loading before show crm conversions */
      setLoading(false);
    } else if (
      tab === "facebook_lead_form" &&
      facebookSettingsResult.result === 0 &&
      facebookSettingsResult.data.length > 0 &&
      facebookSettingsResult.data[0].setup_status === "completed"
    ) {
      setSelectedMenu("facebook_lead_form");
      setSelectedMobileFacebookMenu("facebook_lead_form");
      setLoading(false);
    }
  };

  return (
    <StyledContainer className={"no-back-button"}>
      {/* {isSelfService === false &&
      <Row className="back-btn" alignItems="center" columnSpacing="4px" onClick={backToDashboard}>
        <Icon name="icon-left" color={colors.primary} />
        <Text color={colors.primary}>{t("__back_to_dashboard")}</Text>
      </Row>
      } */}

      {isMobile === false && (
        <Section className="content hide-on-mobile">
          <Row className="panels" responsive>
            <Column width="350px" className="menu-tabs">
              <Text className="menu-label">Google</Text>
              {isSelfService === false ? (
                <Text
                  className={selectedMenu === "google_ads_setup" ? "menu-item -selected" : "menu-item"}
                  onClick={() => {
                    changeTab("google_ads_setup");
                  }}
                >
                  {t("__ads_account")}
                </Text>
              ) : (
                <Text
                  className={selectedMenu === "google_ads_setup" ? "menu-item -selected" : "menu-item"}
                  onClick={() => {
                    changeTab("google_ads_setup");
                  }}
                >
                  {t("__ads_setup_title")}
                </Text>
              )}
              {authAdproResult.data.adwords_managed_service === "Y" ||
              authAdproResult.data.adwords_managed_service === "N" ||
              authAdproResult.data.adwords_managed_service === "U" ? (
                <Text
                  className={`${accountDataResult.result === 0 && accountDataResult.adwords_id ? "" : "-disabled"} ${
                    selectedMenu === "google_ads_offline_conversion" ? "menu-item -selected" : "menu-item"
                  }`}
                  onClick={() => {
                    changeTab("google_ads_offline_conversion");
                  }}
                >
                  {t("__ads_offline_conversion_title")}
                </Text>
              ) : (
                ""
              )}
              {(REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                (businessInfoResult?.data?.business_id &&
                  REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                <Text
                  className={`${accountDataResult.result === 0 && accountDataResult.adwords_id ? "" : "-disabled"} ${
                    selectedMenu === "google_lead_form" ? "menu-item -selected" : "menu-item"
                  }`}
                  onClick={() => {
                    changeTab("google_lead_form");
                  }}
                >
                  Leads
                </Text>
              )}
              <Text className="menu-label">Meta</Text>
              <Text
                className={selectedMenu === "facebook_ads_setup" ? "menu-item -selected" : "menu-item"}
                onClick={() => {
                  changeTab("facebook_ads_setup");
                }}
              >
                {t("__fbads_setup_title")}
              </Text>
              <Text
                className={`${
                  facebookSettingsResult.result === 0 &&
                  facebookSettingsResult.data.length > 0 &&
                  facebookSettingsResult.data[0].setup_status === "completed"
                    ? ""
                    : "-disabled"
                } ${selectedMenu === "facebook_ads_web_conversion" ? "menu-item -selected" : "menu-item"}`}
                onClick={() => {
                  changeTab("facebook_ads_web_conversion");
                }}
              >
                {t("__fbads_web_conversion_title")}
              </Text>
              {(RcrmAppAvailableResult?.data?.foundApp === true ||
                REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                (businessInfoResult?.data?.business_id &&
                  REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                <Text
                  className={`${
                    facebookSettingsResult.result === 0 &&
                    facebookSettingsResult.data.length > 0 &&
                    facebookSettingsResult.data[0].setup_status === "completed"
                      ? ""
                      : "-disabled"
                  } ${selectedMenu === "facebook_ads_crm_conversion" ? "menu-item -selected" : "menu-item"}`}
                  onClick={() => {
                    changeTab("facebook_ads_crm_conversion");
                  }}
                >
                  {t("__fbads_crm_conversion_title")}
                </Text>
              )}
              {(RcrmAppAvailableResult?.data?.foundApp === true ||
                REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                (businessInfoResult?.data?.business_id &&
                  REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                <Text
                  className={`${
                    facebookSettingsResult.result === 0 &&
                    facebookSettingsResult.data.length > 0 &&
                    facebookSettingsResult.data[0].setup_status === "completed"
                      ? ""
                      : "-disabled"
                  } ${selectedMenu === "facebook_lead_form" ? "menu-item -selected" : "menu-item"}`}
                  onClick={() => {
                    changeTab("facebook_lead_form");
                  }}
                >
                  Leads
                </Text>
              )}
            </Column>
            <Column className="content">{RenderContent}</Column>
          </Row>
        </Section>
      )}

      {isMobile === true && (
        <>
          {mobileServiceIndex === 0 ? (
            <Section className="content hide-on-desktop">
              <Row className="panels" responsive>
                <Column width="350px" className="menu-tabs">
                  {isSelfService === false ? (
                    <Text
                      className={selectedMobileGoogleMenu === "google_ads_setup" ? "menu-item -selected" : "menu-item"}
                      onClick={() => {
                        changeTab("google_ads_setup");
                      }}
                    >
                      {t("__ads_account")}
                    </Text>
                  ) : (
                    <Text
                      className={selectedMobileGoogleMenu === "google_ads_setup" ? "menu-item -selected" : "menu-item"}
                      onClick={() => {
                        changeTab("google_ads_setup");
                      }}
                    >
                      {t("__ads_setup_title")}
                    </Text>
                  )}
                  {authAdproResult.data.adwords_managed_service === "Y" ||
                  authAdproResult.data.adwords_managed_service === "N" ||
                  authAdproResult.data.adwords_managed_service === "U" ? (
                    <Text
                      className={`${accountDataResult.result === 0 && accountDataResult.adwords_id ? "" : "-disabled"} ${
                        selectedMobileGoogleMenu === "google_ads_offline_conversion"
                          ? "menu-item -selected"
                          : "menu-item"
                      }`}
                      onClick={() => {
                        changeTab("google_ads_offline_conversion");
                      }}
                    >
                      {t("__ads_offline_conversion_title")}
                    </Text>
                  ) : (
                    ""
                  )}
                  {(REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                    (businessInfoResult?.data?.business_id &&
                      REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                    <Text
                      className={`${accountDataResult.result === 0 && accountDataResult.adwords_id ? "" : "-disabled"} ${
                        selectedMobileGoogleMenu === "google_lead_form" ? "menu-item -selected" : "menu-item"
                      }`}
                      onClick={() => {
                        changeTab("google_lead_form");
                      }}
                    >
                      Leads
                    </Text>
                  )}
                </Column>
                <Column className="content">{RenderGoogleAdsContent}</Column>
              </Row>
            </Section>
          ) : (
            mobileServiceIndex === 1 && (
              <Section className="content hide-on-desktop">
                <Row className="panels" responsive>
                  <Column width="350px" className="menu-tabs">
                    <Text
                      className={
                        selectedMobileFacebookMenu === "facebook_ads_setup" ? "menu-item -selected" : "menu-item"
                      }
                      onClick={() => {
                        changeTab("facebook_ads_setup");
                      }}
                    >
                      {t("__fbads_setup_title")}
                    </Text>
                    <Text
                      className={`${
                        facebookSettingsResult.result === 0 &&
                        facebookSettingsResult.data.length > 0 &&
                        facebookSettingsResult.data[0].setup_status === "completed"
                          ? ""
                          : "-disabled"
                      } ${
                        selectedMobileFacebookMenu === "facebook_ads_web_conversion"
                          ? "menu-item -selected"
                          : "menu-item"
                      }`}
                      onClick={() => {
                        changeTab("facebook_ads_web_conversion");
                      }}
                    >
                      {t("__fbads_web_conversion_title")}
                    </Text>
                    {(RcrmAppAvailableResult?.data?.foundApp === true ||
                      REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                      (businessInfoResult?.data?.business_id &&
                        REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                      <Text
                        className={`${
                          facebookSettingsResult.result === 0 &&
                          facebookSettingsResult.data.length > 0 &&
                          facebookSettingsResult.data[0].setup_status === "completed"
                            ? ""
                            : "-disabled"
                        } ${
                          selectedMobileFacebookMenu === "facebook_ads_crm_conversion"
                            ? "menu-item -selected"
                            : "menu-item"
                        }`}
                        onClick={() => {
                          changeTab("facebook_ads_crm_conversion");
                        }}
                      >
                        {t("__fbads_crm_conversion_title")}
                      </Text>
                    )}
                    {(RcrmAppAvailableResult?.data?.foundApp === true ||
                      REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                      (businessInfoResult?.data?.business_id &&
                        REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                      <Text
                        className={`${
                          facebookSettingsResult.result === 0 &&
                          facebookSettingsResult.data.length > 0 &&
                          facebookSettingsResult.data[0].setup_status === "completed"
                            ? ""
                            : "-disabled"
                        } ${selectedMobileFacebookMenu === "facebook_lead_form" ? "menu-item -selected" : "menu-item"}`}
                        onClick={() => {
                          changeTab("facebook_lead_form");
                        }}
                      >
                        Leads
                      </Text>
                    )}
                  </Column>
                  <Column className="content">{RenderFacebookAdsContent}</Column>
                </Row>
              </Section>
            )
          )}
        </>
      )}

      <Toast
        show={showNoAdsToastResult.data.value}
        message={t("__ads_setup_no_available_ads")}
        bgColor="#F44336"
        callback={noAdsToastCallback}
      ></Toast>
    </StyledContainer>
  );
};

export default Setting;

const AdsAccountSetting = () => {
  const [t] = useTranslation();
  const history = useHistory();
  const rmpToken = sessionStorage.getItem("rmp_token");
  const [isSelectAdsPage, setIsSelectAdsPage] = useState(true);
  const [selectedGoogleAds, setSelectedGoogleAds] = useState();
  const [selectedFacebookAds, setSelectedFacebookAds] = useState();
  const [loading, setLoading] = useState(true);
  const adsList = useSelector((state) => settingSelectors.allAdsList(state));
  const changeConnectedAdsResult = useSelector((state) => settingSelectors.changeConnectedAdsResult(state));
  const businessInfoResult = useSelector((state) => googleSelectors.getBusinessInfoResult(state));
  const accountDataResult = useSelector((state) => googleSelectors.getAccountDataResult(state));
  const authAdproResult = useSelector((state) => mainSelectors.authAdproResult(state));
  const dispatch = useDispatch();

  const googleAdsList = [];
  const facebookAdsList = [];

  let connectedGoogleAds, connectedFacebookAds, changeAdsErrorText;

  useEffect(() => {
    dispatch(settingAction.GetAllAdsList());
  }, [dispatch, businessInfoResult]);

  useEffect(() => {
    if (adsList.connectedAds && adsList.connectedAds.length > 0) {
      const account = _find(adsList.connectedAds, { type: "g_ads" });

      if (
        authAdproResult.data.adwords_managed_service === "Y" &&
        account &&
        account.ads_account_id !== businessInfoResult.data.adwords_id
      ) {
        dispatch(
          googleActions.setBusinessAdwordsId({
            token: rmpToken,
            business_id: businessInfoResult.data.business_id,
            adwords_id: account.ads_account_id,
            adwords_managed_service: "Y",
          }),
        );

        dispatch(
          googleActions.setBusinessAdwordsName({
            token: rmpToken,
            business_id: businessInfoResult.data.business_id,
            adwords_name: account.account_name,
          }),
        );
      }
    }

    setLoading(false);
  }, [adsList, businessInfoResult]);

  if (adsList.allAds) {
    adsList.allAds.forEach((item, key) => {
      if (item.type === "g_ads") {
        googleAdsList.push({
          title: item.account_name + " (" + item.ads_account_id + ")",
          value: item.ads_account_id,
          name: item.account_name,
        });
      } else if (item.type === "fb") {
        facebookAdsList.push({
          title: item.account_name + " (" + item.ads_account_id + ")",
          value: item.ads_account_id,
          name: item.account_name,
        });
      }
    });
  }

  if (adsList.connectedAds) {
    let account = _find(adsList.connectedAds, { type: "g_ads" });
    if (account) {
      connectedGoogleAds = {
        title: account.account_name + " (" + account.ads_account_id + ")",
        value: account.ads_account_id,
        name: account.account_name,
      };
    }

    account = _find(adsList.connectedAds, { type: "fb" });
    if (account) {
      connectedFacebookAds = {
        title: account.account_name + " (" + account.ads_account_id + ")",
        value: account.ads_account_id,
        name: account.account_name,
      };
    }
  }

  if (selectedGoogleAds) {
    connectedGoogleAds = selectedGoogleAds;
  } else if (!connectedGoogleAds && googleAdsList.length > 0) {
    connectedGoogleAds = googleAdsList[0];
  }
  if (selectedFacebookAds) {
    connectedFacebookAds = selectedFacebookAds;
  } else if (!connectedFacebookAds && facebookAdsList.length > 0) {
    connectedFacebookAds = facebookAdsList[0];
  }

  if (!isSelectAdsPage) {
    if (changeConnectedAdsResult.result) {
      changeAdsErrorText = t("__change_ads_error");
    } else if (changeConnectedAdsResult.result === 0) {
      dispatch(settingAction.ClearChangeConnectedAdsResult());
      history.replace("/");
      return null;
    }
  }

  const onSaveAds = () => {
    setIsSelectAdsPage(false);
  };

  const onConfirm = () => {
    if (authAdproResult.data.adwords_managed_service === "Y") {
      dispatch(
        googleActions.setBusinessAdwordsId({
          token: rmpToken,
          business_id: businessInfoResult.data.business_id,
          adwords_id: _get(connectedGoogleAds, ["value"]),
          adwords_managed_service: "Y",
        }),
      );

      dispatch(
        googleActions.setBusinessAdwordsName({
          token: rmpToken,
          business_id: businessInfoResult.data.business_id,
          adwords_name: _get(connectedGoogleAds, ["name"]),
        }),
      );
    }

    adsList.connectedAds = [];
    dispatch(
      settingAction.ChangeConnectedAds(_get(connectedGoogleAds, ["value"]), _get(connectedFacebookAds, ["value"])),
    );
  };

  const onCancel = () => {
    setIsSelectAdsPage(true);
    setSelectedGoogleAds();
    setSelectedFacebookAds();
    dispatch(settingAction.ClearChangeConnectedAdsResult());
  };

  return (
    <>
      <AdsAccountContainer className={authAdproResult.result !== 0 ? "no-back-button" : ""}>
        <div className="body">
          {loading === true ? (
            <Loading />
          ) : (
            <AdsSelector
              googleAdsList={googleAdsList}
              facebookAdsList={facebookAdsList}
              connectedGoogleAds={connectedGoogleAds}
              connectedFacebookAds={connectedFacebookAds}
              onSelectGoogle={setSelectedGoogleAds}
              onSelectFacebook={setSelectedFacebookAds}
              onSaveAds={onSaveAds}
              isSetting
            />
          )}
        </div>
        <div className="footer">
          <Button onClick={onCancel} type="link" className="cancel-btn">
            {t("__cancel")}
          </Button>
          <Button onClick={onSaveAds} disabled={!(selectedGoogleAds || selectedFacebookAds)}>
            {t("__save")}
          </Button>
        </div>
      </AdsAccountContainer>
      {!isSelectAdsPage && (
        <>
          <DialogContainer>
            <Section className="content">
              <AdsConfirmation
                selectedGoogleAds={connectedGoogleAds}
                selectedFacebookAds={connectedFacebookAds}
                onConfirm={onConfirm}
                onCancel={onCancel}
                errorText={changeAdsErrorText}
                isSetting
              />
            </Section>
          </DialogContainer>
          <Backdrop />
        </>
      )}
    </>
  );
};

const StyledContainer = styled.div`
  max-width: 900px;
  margin: auto;
  height: calc(100vh - 140px);
  height: calc(100dvh - 140px);
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  .hide-on-desktop {
    display: none;
  }

  &.no-back-button {
    height: calc(100vh - 110px);
    height: calc(100dvh - 110px);
  }

  > .content {
    height: 100%;
    box-sizing: border-box;

    > .panels {
      margin: -16px;
      height: calc(100% + 32px);
      overflow-y: unset;

      > .column {
        height: 100%;
        overflow-y: auto;

        &:not(:last-child) {
          border-right: 1px solid ${colors.line};
          margin: 0;
        }
      }

      > .content {
        padding: 16px;
        box-sizing: border-box;
      }

      .menu-label {
        padding: 16px;
        font-weight: 700;
        font-size: 12px;
        color: ${colors.subText};
      }

      .menu-item {
        padding: 10px 10px 10px 30px;
        cursor: pointer;

        &.-selected {
          border-left: 4px solid ${colors.primary};
          color: ${colors.primary};
          padding-left: 26px;
        }

        &.-disabled {
          cursor: not-allowed;
          color: #999;
        }
      }
    }
  }

  @media screen and (max-width: 834px) {
    height: calc(100% - 60px);

    &.no-back-button {
      height: calc(100% - 16px);
    }

    > .content {
      height: 100%;
      max-height: calc(100% - 5px);
      box-sizing: border-box;
      margin-bottom: 0px;

      > .panels {
        > .column {
          box-sizing: border-box;
          overflow-y: auto;
          height: 100%;

          &:first-child {
            height: auto;
            border: none;
            border-bottom: 1px solid ${colors.line};
            width: 100%;
          }
        }

        .menu-tabs {
          display: flex;
        }

        .menu-item {
          padding: 10px;
          width: 50%;
          box-sizing: border-box;
          text-align: center;

          &.-selected {
            border-left: none;
            border-bottom: 2px solid ${colors.primary};
            padding-left: 10px;
          }
        }

        .dropdown-button {
          min-width: unset;
        }
      }
    }

    .hide-on-mobile {
      display: none;
    }

    .hide-on-desktop {
      display: block;
    }
  }
`;

const AdsAccountContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  &.no-back-button {
    height: calc(100% - 32px);
  }

  > .body {
    height: 100%;
    overflow: auto;
  }

  > .footer {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid ${colors.line};
    padding: 16px 16px 0 16px;
    margin: 0 -16px;

    button {
      margin: 0 0 0 16px;
    }
  }

  @media screen and (max-width: 834px) {
    height: 100%;
  }
`;

const DialogContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  > .content {
    z-index: 999;
    box-shadow: none;
    width: 360px;
  }

  @media screen and (max-width: 834px) {
    padding: 8px;

    > .content {
      width: 100%;
    }
  }
`;

const Backdrop = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 998;
`;

const MobileLabel = styled(TextHeader)`
  font-size: 1.25rem;
  margin: 20px 0px 10px;
`;

const MobileDropdownServiceContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
`;
